import {
  type CSSProperties,
  type FormEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';

type SignupStatus = 'idle' | 'loading' | 'success' | 'error';

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface NewsletterSignupProps {
  triggerClassName?: string;
  triggerLabel?: string;
  triggerStyle?: CSSProperties;
}

export function NewsletterSignup({
  triggerClassName = 'fixed right-4 z-30 cursor-pointer border-0 bg-transparent p-0 text-right text-base font-light text-black transition-opacity duration-200 ease-in-out hover:opacity-70 md:text-xl',
  triggerLabel = 'Newsletter',
  triggerStyle = { bottom: 'calc(env(safe-area-inset-bottom) + 1rem)' },
}: NewsletterSignupProps) {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [status, setStatus] = useState<SignupStatus>('idle');
  const [message, setMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const normalizedFirstName = firstName.trim();
  const normalizedEmail = email.trim().toLowerCase();
  const canSubmit = emailPattern.test(normalizedEmail);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);

    if (status === 'success') {
      setStatus('idle');
      setMessage('');
    }
  }, [status]);

  useEffect(() => {
    if (!isModalOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCloseModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleCloseModal, isModalOpen]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!canSubmit) {
      setStatus('error');
      setMessage('Enter a valid email address.');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: normalizedFirstName,
          email: normalizedEmail,
          company,
        }),
      });

      const body = (await response.json().catch(() => null)) as {
        error?: string;
      } | null;

      if (!response.ok) {
        throw new Error(body?.error ?? 'Unable to join.');
      }

      setFirstName('');
      setEmail('');
      setCompany('');
      setStatus('success');
      setMessage('');
    } catch (error) {
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Unable to join.');
    }
  };

  return (
    <>
      <button
        type="button"
        className={triggerClassName}
        style={triggerStyle}
        onClick={() => setIsModalOpen(true)}
      >
        {triggerLabel}
      </button>

      {isModalOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/35 p-4">
          <button
            type="button"
            className="absolute inset-0 cursor-default border-0 bg-transparent p-0"
            aria-label="Close newsletter signup"
            onClick={handleCloseModal}
          />
          {status === 'success' ? (
            <section
              role="dialog"
              aria-modal="true"
              aria-label="Newsletter signup success"
              className="relative z-10 w-full max-w-[620px] bg-white px-5 py-6 text-base font-light text-black md:px-10 md:py-9 md:text-xl"
            >
              <p className="m-0 leading-snug">Thank you for joining.</p>
            </section>
          ) : (
            <section
              role="dialog"
              aria-modal="true"
              aria-label="Newsletter signup"
              className="relative z-10 w-full max-w-[620px] bg-white px-5 py-6 text-base font-light text-black md:px-10 md:py-9 md:text-xl"
            >
              <p className="m-0 mb-8 max-w-[520px] leading-snug">
                Provide your name and email to receive monthly updates on my
                studio practice.
              </p>
              <form
                className="flex flex-col gap-4"
                noValidate
                onSubmit={handleSubmit}
              >
                <div>
                  <label className="sr-only" htmlFor="newsletter-first-name">
                    First name
                  </label>
                  <input
                    id="newsletter-first-name"
                    name="firstName"
                    type="text"
                    autoComplete="given-name"
                    placeholder="First name"
                    value={firstName}
                    disabled={status === 'loading'}
                    className="h-12 w-full appearance-none border border-black bg-transparent px-3 text-base font-light text-black placeholder:text-black/45 focus:outline-none disabled:opacity-50 md:h-14 md:px-4 md:text-xl"
                    onChange={(event) => {
                      setFirstName(event.target.value);
                      if (status !== 'loading') {
                        setStatus('idle');
                        setMessage('');
                      }
                    }}
                  />
                </div>
                <div>
                  <label className="sr-only" htmlFor="newsletter-email">
                    Newsletter email
                  </label>
                  <input
                    id="newsletter-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder="Email"
                    value={email}
                    disabled={status === 'loading'}
                    className="h-12 w-full appearance-none border border-black bg-transparent px-3 text-base font-light text-black placeholder:text-black/45 focus:outline-none disabled:opacity-50 md:h-14 md:px-4 md:text-xl"
                    onChange={(event) => {
                      setEmail(event.target.value);
                      if (status !== 'loading') {
                        setStatus('idle');
                        setMessage('');
                      }
                    }}
                  />
                </div>
                <label className="sr-only" htmlFor="newsletter-company">
                  Company
                </label>
                <input
                  id="newsletter-company"
                  name="company"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={company}
                  className="hidden"
                  onChange={(event) => setCompany(event.target.value)}
                />
                <button
                  type="submit"
                  disabled={!canSubmit || status === 'loading'}
                  className="h-12 w-full cursor-pointer border border-black bg-black px-3 text-center text-base font-light text-white transition-opacity duration-200 ease-in-out hover:opacity-70 disabled:cursor-default disabled:opacity-40 md:h-14 md:px-4 md:text-xl"
                >
                  {status === 'loading' ? 'Submitting' : 'Submit'}
                </button>
              </form>
              <p
                aria-live="polite"
                className="mt-2 min-h-3 text-sm font-light leading-none text-black md:text-base"
              >
                {message}
              </p>
            </section>
          )}
        </div>
      )}
    </>
  );
}
